import clsx from "clsx";
import React, { useState } from "react";
import {
  Box,
  BoxProps,
  Typography,
  Button,
  Dialog,
  IconButton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Celebration, Close } from "@mui/icons-material";
import { ReactComponent as Snowflakes } from "../../icons/snowflakes.svg";
import { ReactComponent as Star } from "../../icons/yearInReviewStar.svg";
import { ReactComponent as Ball } from "../../icons/yearInReviewBall.svg";

import YearInReview from "../year-in-review";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useCurrentUserRole } from "../../hooks/useCurrentUser";

export const YEAR_IN_REVIEW_BANNER_SPACING = 6.75;

const useStyles = makeStyles((theme) => ({
  "@global": {
    "#root": {
      marginTop: (props: { preview: boolean; isVisible: boolean }) =>
        props.preview || !props.isVisible
          ? "initial"
          : theme.spacing(YEAR_IN_REVIEW_BANNER_SPACING),
    },
    ".MuiDrawer-docked > div": {
      paddingTop: (props: { preview: boolean; isVisible: boolean }) =>
        props.preview || !props.isVisible
          ? "initial"
          : theme.spacing(YEAR_IN_REVIEW_BANNER_SPACING),
    },
    ".mobile-toast .Toastify__toast": {
      top: `${theme.spacing(YEAR_IN_REVIEW_BANNER_SPACING)} !important`,
    },
    ".MuiAppBar-positionFixed": {
      marginTop: (props: { preview: boolean; isVisible: boolean }) =>
        props.preview || !props.isVisible
          ? "initial"
          : theme.spacing(YEAR_IN_REVIEW_BANNER_SPACING),
    },
  },

  root: {
    background: "#222222",
    height: `calc(${theme.spacing(YEAR_IN_REVIEW_BANNER_SPACING)} + max(var(--safe-area-inset-top), 0px))`,
    zIndex: theme.zIndex.banner,
    position: "fixed",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2),
    paddingTop: `max(var(--safe-area-inset-top), 0px)`,
    top: 0,
    left: 0,
    right: 0,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    [theme.breakpoints.down("sm")]: {
      paddingTop: `calc(max(var(--safe-area-inset-top), 0px) + ${theme.spacing(1)})`,
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(0.5),
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: "-150%",
      left: "-50%",
      width: "200%",
      height: "400%",
      background:
        "radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, rgba(6, 182, 212, 0.4) 50%, transparent 70%)",
      pointerEvents: "none",
    },
  },

  content: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
    },
  },

  header: {
    color: theme.palette.common.white,
    fontWeight: "bold",
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
  },

  yearHighlight: {
    background: "linear-gradient(135deg, #ec4899 0%, #06b6d4 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: 900,
  },

  subtitle: {
    color: "rgba(255, 255, 255, 0.9)",
    fontSize: 14,
    fontWeight: 400,
    [theme.breakpoints.down("sm")]: {
      fontSize: 11,
      lineHeight: 1.1,
    },
  },
  desktopSubtitle: {
    display: "block",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  mobileSubtitle: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      fontSize: 11,
      lineHeight: 1.2,
    },
  },

  actions: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    gap: "12px",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      gap: "4px",
    },
  },

  button: {
    color: theme.palette.common.white,
    borderColor: "rgba(255, 255, 255, 0.5)",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "8px",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderColor: theme.palette.common.white,
    },
    [theme.breakpoints.down("sm")]: {
      padding: "2px 8px",
      fontSize: "12px",
      minWidth: "auto",
    },
  },

  closeButton: {
    color: "rgba(255, 255, 255, 0.5)",
    padding: theme.spacing(0.5),
    "&:hover": {
      color: theme.palette.common.white,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },

  dialog: {
    "& .MuiDialog-paper": {
      maxWidth: "100vw",
      maxHeight: "100vh",
      margin: 0,
      width: "100%",
      height: "100%",
      borderRadius: 0,
    },
  },
  toysBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
      gap: "4px",
    },
  },

  toyIcon: {
    width: 32,
    height: "auto",
    opacity: 0.9,
    filter: "drop-shadow(0 0 5px rgba(255,255,255,0.2))",
    [theme.breakpoints.down("sm")]: {
      width: 20,
    },
  },
  toySnowflake: {
    marginTop: 6,
    [theme.breakpoints.down("sm")]: { marginTop: 2 },
  },
  toyStar: {
    marginTop: 4,
    width: 26,
    [theme.breakpoints.down("sm")]: { width: 18, marginTop: 1 },
  },
  toyBall: {
    marginTop: 0,
    width: 24,
    [theme.breakpoints.down("sm")]: { width: 16 },
  },
}));

export interface YearInReviewBannerProps extends BoxProps {}

export function YearInReviewBanner(props: YearInReviewBannerProps) {
  const { className, ...other } = props;
  const userRole = useCurrentUserRole();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [previewQueryParam] = useState(false); // You can connect this to useQueryParam if needed
  const preview = previewQueryParam;
  const s = useStyles({ preview, isVisible });
  const [trackEvent] = useAnalytics();

  const handleOpenModal = () => {
    trackEvent("YearInReviewBanner_OpenModal", { userRole });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  if (preview || !isVisible) return null;

  return (
    <>
      {!isModalOpen && (
        <Box className={clsx(s.root, className)} {...other}>
          <div className={s.content}>
            <div>
              <Typography className={s.header}>
                Your <span className={s.yearHighlight}>2025</span> year in
                review is ready!
              </Typography>
              <Typography className={clsx(s.subtitle, s.desktopSubtitle)}>
                Celebrate your achievements and see how far you've come
              </Typography>
              <Typography className={clsx(s.subtitle, s.mobileSubtitle)}>
                Check your achievements!
              </Typography>
            </div>
          </div>
          <div className={s.actions}>
            <Box
              className={s.toysBox}
              sx={{ display: { xs: "none", sm: "flex" } }}
            >
              <Snowflakes className={clsx(s.toyIcon, s.toySnowflake)} />
              <Star className={clsx(s.toyIcon, s.toyStar)} />
              <Ball className={clsx(s.toyIcon, s.toyBall)} />
            </Box>
            <Button
              className={s.button}
              onClick={handleOpenModal}
              variant="outlined"
              size="small"
            >
              View Your Year
            </Button>
            <IconButton
              className={s.closeButton}
              onClick={() => setIsVisible(false)}
              size="small"
            >
              <Close fontSize="small" />
            </IconButton>
          </div>
        </Box>
      )}

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        className={s.dialog}
        fullScreen
      >
        <YearInReview onClose={handleCloseModal} />
      </Dialog>
    </>
  );
}
